import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import { Box, IconButton, TextField } from "@mui/material"
import { ImageDropzone } from "components/ImageDropZone/ImageDropzone"
import { DataNode, FileType, FileUploadInput, File as ImageFile, User } from "graphql/types"
import { useStoreActions } from "hooks/storeHooks"
import { emptyPerson } from "store/store"
import { createCaption } from "utils/createCaption"
import { getImageURL } from "utils/getFileURL"
import { useLocalStorage } from "utils/useLocalStorage"

interface ImageManagerProps {
  node: DataNode
  // eslint-disable-next-line no-unused-vars
  setNode: (node: DataNode) => void
}

const ImageManager: React.FC<ImageManagerProps> = ({ node, setNode }) => {
  const [user] = useLocalStorage<User>("user", emptyPerson)
  const { uploadFile } = useStoreActions((actions) => actions)

  const onImageFileChosen = async (files: File[]) => {
    const uploads: FileUploadInput[] = await Promise.all(
      files.map(async (file) => {
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.readAsDataURL(file)
        })

        return {
          id: "NEW",
          base64,
          name: file.name,
          type: FileType.IMAGE,
          createdAt: new Date().toISOString(),
          createdBy: user.id,
          filename: file.name, // Verwende den ursprünglichen Dateinamen
        }
      })
    )

    const uploadResults = await Promise.all(
      uploads.map(async (upload) => {
        const result = await uploadFile(upload)
        console.log("result", result)
        return { ...upload, id: result }
      })
    )

    const imgs: ImageFile[] = uploadResults.map((upload) => ({
      id: upload.id,
      description: upload.description,
      createdAt: new Date().toISOString(),
      createdBy: user.id,
      type: FileType.IMAGE,
      fileURL: upload.base64,
      name: upload.name,
      filename: upload.filename,
    }))
    setNode({
      ...node,
      image: [...node.image, ...imgs.map((img) => img.id)],
      imageCaption: [...node.imageCaption, ...imgs.map((img) => createCaption(img))],
    })
  }

  const onFileReject = (rejections: any) => {
    console.error("File rejected", rejections)
  }

  const removeImage = (id: string) => {
    const captionIndex = node.image.indexOf(id)
    const updatedImage = node.image.filter((img) => img !== id)
    const updatedCaption = node.imageCaption.filter((_, index) => index !== captionIndex)
    setNode({ ...node, image: updatedImage, imageCaption: updatedCaption })
  }

  const updateImageName = (id: string, newName: string) => {
    const updateIndex = node.image.indexOf(id)
    const updatedCaption = node.imageCaption.map((caption, index) =>
      index === updateIndex ? newName : caption
    )

    setNode({ ...node, imageCaption: updatedCaption })
  }

  const onDragEnd = (result: DropResult<string>) => {
    if (!result.destination) return
    const items = Array.from(node.image)
    const [reordered] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reordered)
    setNode({ ...node, image: items })
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <ImageDropzone
        onImageFileChosen={onImageFileChosen}
        onFileReject={onFileReject}
        image={null}
        accept={{ "image/*": [] }}
        setImage={() => {}}
      />
      <Box mt={2}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="images" direction="vertical">
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  display: "grid",
                  gridTemplateColumns: node.image.length > 1 ? "repeat(2, 1fr)" : "1fr",
                  gap: 2,
                }}
              >
                {node.image.map((img, index) => (
                  <Draggable key={img} draggableId={img} index={index}>
                    {(provided) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap={1}
                      >
                        <img
                          src={getImageURL(img)}
                          alt={node.imageCaption[index]}
                          width={100}
                          height={100}
                          style={{ objectFit: "cover" }}
                        />
                        <Box>
                          <TextField
                            label="Beschriftung"
                            value={node.imageCaption[index]}
                            onChange={(e) => updateImageName(img, e.target.value)}
                            variant="outlined"
                            size="small"
                          />
                          <IconButton onClick={() => removeImage(img)}>
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    </Box>
  )
}

export default ImageManager
